/**
 * Come si legge un TIFF piramidale da 60 Mpixel senza scaricarlo.
 *
 * **I tasselli non passano dal servizio** (GI-95): l'archivio li serve per
 * intervalli di byte, e il visualizzatore prende solo i pochi che si vedono.
 * Mettere l'API sulla strada di ogni tassello ne farebbe il collo di bottiglia
 * della giornata dell'operatore.
 *
 * **Il tassello arriva al navigatore come JPEG, non come pixel.** Dentro il
 * TIFF ogni tassello *e' gia'* un JPEG (libvips lo scrive con
 * `compression="jpeg"`): decomprimerlo in JavaScript significherebbe rifare in
 * un interprete un lavoro che il navigatore fa con codice nativo, e su un
 * fotogramma da 60 Mpixel la differenza si vede a occhio.
 *
 * L'unica cura serve perche' il TIFF conserva le tabelle di quantizzazione e
 * di Huffman **una volta sola**, in `JPEGTables`, e le toglie dai singoli
 * tasselli: un tassello preso cosi' com'e' non e' un JPEG apribile. Si
 * ricompone innestando le tabelle nel flusso — e' la ragione per cui esiste
 * `asJpeg`.
 */
import { fromCustomClient } from 'geotiff'
import type { BaseClient } from 'geotiff'

import { get } from './api'

const SOI = 2 // il marcatore d'inizio immagine, due byte

/** Quanto conta una richiesta per il navigatore: l'indice del fotogramma che
 *  si sta aprendo prima di tutto, i tasselli del riscaldamento dopo tutto. */
export type Priority = 'high' | 'low' | 'auto'

/** `fetch` con il suggerimento di priorita', dove il navigatore lo capisce. */
function fetchWith(url: string, init: RequestInit, priority: Priority): Promise<Response> {
  return fetch(url, { ...init, ...(priority === 'auto' ? {} : { priority }) } as RequestInit)
}

/* La forma che geotiff si aspetta da una risposta (`BaseResponse`) e da un
 * client (`BaseClient`): si ricalca senza ereditarla, perche' i tipi che
 * geotiff pubblica per le due basi sono generati e sbagliati (`status: void`,
 * `getData(): ArrayBuffer`), e un `extends` non compila. */
export class RangeResponse {
  constructor(
    private readonly response: Response,
    private readonly overrideStatus: number | null = null,
    private readonly empty = false,
  ) {}

  get ok(): boolean {
    return this.status >= 200 && this.status <= 299
  }

  get status(): number {
    return this.overrideStatus ?? this.response.status
  }

  getHeader(name: string): string | null {
    return this.response.headers.get(name)
  }

  async getData(): Promise<ArrayBuffer> {
    return this.empty ? new ArrayBuffer(0) : this.response.arrayBuffer()
  }
}

/**
 * Il client con cui geotiff legge l'indice, al posto del suo.
 *
 * **Un archivio che non dice quanto e' lungo il file.** geotiff impara la
 * lunghezza da `Content-Range`, e la usa per non chiedere blocchi oltre la
 * fine. Ma quel campo il navigatore lo lascia leggere solo se il deposito lo
 * espone (CORS), e su un deposito configurato a mano mancava: geotiff chiedeva
 * un blocco dopo la fine, l'archivio rispondeva `416`, e il fotogramma non si
 * apriva — solo quelli il cui indice sta vicino alla fine, cioe' a caso
 * (staging, 8.9.2026). Il deposito ora lo espone dal codice
 * (`allow_browser_reads`), ma la pagina non deve dipendere da una regola
 * altrui: un `416` oltre la fine sono zero byte, e cosi' si risponde.
 *
 * **L'indice si chiede con priorita' alta.** E' la catena di letture in fila
 * che tiene il fotogramma su «Leggo…»: ogni viaggio che aspetta dietro ai
 * tasselli del fotogramma di prima e' un secondo in piu' sulla scritta.
 */
export class RangeClient {
  constructor(readonly url: string) {}

  async request({
    headers,
    signal,
  }: { headers?: HeadersInit; signal?: AbortSignal } = {}): Promise<RangeResponse> {
    const response = await fetchWith(this.url, { headers, signal }, 'high')
    if (response.status === 416) return new RangeResponse(response, 206, true)
    return new RangeResponse(response)
  }
}

/** Un livello della piramide: misure, griglia dei tasselli e dove stanno. */
export interface Level {
  width: number
  height: number
  tile: number
  columns: number
  offsets: number[]
  lengths: number[]
}

export interface Pyramid {
  /** Dal piu' fitto (indice 0, piena risoluzione) al piu' rado. */
  levels: Level[]
  tables: Uint8Array | null
  /** I byte grezzi di un tassello, presi dall'archivio. */
  bytes(level: number, index: number, priority?: Priority): Promise<Uint8Array<ArrayBuffer>>
}

interface Directory {
  ImageWidth?: number
  ImageLength?: number
  TileWidth?: number
  TileLength?: number
  TileOffsets?: number[]
  TileByteCounts?: number[]
  JPEGTables?: number[] | Uint8Array
}

/**
 * Ricompone un JPEG apribile dalle tabelle comuni e dai byte del tassello.
 *
 * Le tabelle finiscono con EOI e il tassello comincia con SOI: incollati
 * cosi' come sono darebbero un flusso con un marcatore di fine a meta'. Si
 * toglie l'EOI alle prime e il SOI ai secondi.
 */
export function asJpeg(
  tables: Uint8Array | null,
  tile: Uint8Array<ArrayBuffer>,
): Uint8Array<ArrayBuffer> {
  if (!tables || tables.length < 4) return tile
  const head = tables.subarray(0, tables.length - 2) // senza EOI
  const body = tile.subarray(SOI) // senza SOI
  const whole = new Uint8Array(head.length + body.length)
  whole.set(head, 0)
  whole.set(body, head.length)
  return whole
}

/**
 * Il livello del TIFF che corrisponde al livello chiesto dal visualizzatore.
 *
 * Vanno in versi opposti: per OpenSeadragon lo zero e' l'immagine piu' piccola,
 * nel TIFF e' quella a piena risoluzione. Confonderli non da' un errore — da'
 * un fotogramma che si apre sfocato e si ingrandisce all'incontrario.
 */
export function levelOf(osdLevel: number, count: number): number {
  return count - 1 - osdLevel
}

/** L'indice del tassello nella griglia del suo livello, per riga. */
export function tileIndex(level: Level, column: number, row: number): number {
  return row * level.columns + column
}

/* Le piramidi aperte, per indirizzo. Il banco passa da un candidato al
 * successivo e il fotogrammario da un fotogramma al vicino: riaprire l'indice
 * gia' letto e' un viaggio all'archivio per niente, e il riscaldamento
 * (`prewarm`) ha senso solo se cio' che scalda resta (GI-257). */
const opened = new Map<string, Promise<Pyramid>>()

/** Dove il servizio serve l'indice di un fotogramma (GI-269). */
export function indexPath(photoId: string): string {
  return `/photos/${photoId}/pyramid`
}

/**
 * Apre la piramide leggendone il solo indice, non i pixel. Una volta per
 * indirizzo: la seconda richiesta trova la prima.
 *
 * **L'indice si chiede al servizio, se si sa dove (GI-269).** Ricostruirlo
 * dall'archivio costa un viaggio per livello, in fila — sei o sette, da 0,3 a
 * 2,9 s ciascuno su un archivio lento (staging, 8.9.2026): dieci secondi di
 * «Leggo il fotogramma…». Il servizio lo tiene gia' letto e lo da' in un
 * viaggio solo. Se non ce l'ha, o non risponde, si torna alla catena: un
 * fotogramma lento e' meglio di un fotogramma che non si apre.
 */
export function open(url: string, index: string | null = null): Promise<Pyramid> {
  const found = opened.get(url)
  if (found !== undefined) return found
  const opening = (index === null ? Promise.reject(new Error('senza indice')) : served(index))
    .catch(() => read(url))
    .then((shape) => pyramidOf(url, shape))
  opened.set(url, opening)
  opening.catch(() => opened.delete(url))
  return opening
}

/** Quanti livelli, dal piu' rado, si scaldano prima che il fotogramma si
 *  apra: i due piu' radi sono cinque tasselli e coprono tutto il fotogramma
 *  alla scala con cui si apre. */
const COARSE_LEVELS = 2

/**
 * Scalda una piramide: apre l'indice e prende i tasselli dei livelli piu'
 * radi, quelli con cui il fotogramma compare. Chi lo apre dopo li trova gia'
 * in memoria e vede il fotogramma intero subito, non a meta' (GI-257).
 * Un guasto qui non e' un guasto: e' un riscaldamento mancato.
 */
export async function prewarm(url: string, index: string | null = null): Promise<void> {
  try {
    const pyramid = await open(url, index)
    const from = Math.max(0, pyramid.levels.length - COARSE_LEVELS)
    const jobs: Promise<unknown>[] = []
    for (let level = from; level < pyramid.levels.length; level += 1) {
      const chosen = pyramid.levels[level]
      if (!chosen) continue
      for (let index = 0; index < chosen.offsets.length; index += 1) {
        // Dopo tutto il resto: il riscaldamento non deve rubare la banda al
        // fotogramma che si sta guardando.
        jobs.push(pyramid.bytes(level, index, 'low').catch(() => undefined))
      }
    }
    await Promise.all(jobs)
  } catch {
    // Vedi sopra: niente da dire a chi lavora.
  }
}

/** La forma della piramide, da chiunque arrivi: livelli e tabelle. */
interface Shape {
  levels: Level[]
  tables: Uint8Array | null
}

/** Cio' che il servizio risponde a `indexPath`: la stessa forma, con le tabelle
 *  in base64 e senza le colonne, che si contano qui. */
interface ServedIndex {
  levels: Omit<Level, 'columns'>[]
  tables: string | null
}

async function served(index: string): Promise<Shape> {
  const got = await get<ServedIndex>(index)
  if (!Array.isArray(got.levels) || got.levels.length === 0) {
    throw new Error('la piramide non ha livelli a tasselli')
  }
  return {
    levels: got.levels.map((level) => ({ ...level, columns: Math.ceil(level.width / level.tile) })),
    tables: got.tables ? Uint8Array.from(atob(got.tables), (c) => c.charCodeAt(0)) : null,
  }
}

async function read(url: string): Promise<Shape> {
  const tiff = await fromCustomClient(new RangeClient(url) as unknown as BaseClient)
  const count = await tiff.getImageCount()

  const levels: Level[] = []
  let tables: Uint8Array | null = null

  for (let index = 0; index < count; index += 1) {
    const image = await tiff.getImage(index)
    const directory = image.fileDirectory as Directory
    const width = directory.ImageWidth ?? 0
    const tile = directory.TileWidth ?? 0
    if (!tile || !directory.TileOffsets) {
      // Un livello non a tasselli non e' servibile per intervalli: meglio
      // saltarlo che offrirlo e fallire a ogni richiesta.
      continue
    }
    if (!tables && directory.JPEGTables) {
      tables = Uint8Array.from(directory.JPEGTables)
    }
    levels.push({
      width,
      height: directory.ImageLength ?? 0,
      tile,
      columns: Math.ceil(width / tile),
      offsets: Array.from(directory.TileOffsets),
      lengths: Array.from(directory.TileByteCounts ?? []),
    })
  }

  if (levels.length === 0) throw new Error('la piramide non ha livelli a tasselli')
  return { levels, tables }
}

function pyramidOf(url: string, { levels, tables }: Shape): Pyramid {
  // I byte gia' presi, per tassello: il riscaldamento li mette qui e il
  // visualizzatore li trova. Un guasto non resta in memoria: si riprova.
  const taken = new Map<string, Promise<Uint8Array<ArrayBuffer>>>()

  return {
    levels,
    tables,
    bytes(level: number, index: number, priority: Priority = 'auto'): Promise<Uint8Array<ArrayBuffer>> {
      const key = `${level}:${index}`
      const found = taken.get(key)
      if (found !== undefined) return found
      const fetching = (async () => {
        const chosen = levels[level]
        if (!chosen) throw new Error(`livello ${level} inesistente`)
        const start = chosen.offsets[index]
        const length = chosen.lengths[index]
        if (start === undefined || length === undefined) {
          throw new Error(`tassello ${index} inesistente al livello ${level}`)
        }
        // Un solo intervallo per tassello: l'archivio non deve leggere l'oggetto
        // intero per consegnarne mezzo megabyte.
        const response = await fetchWith(
          url,
          { headers: { Range: `bytes=${start}-${start + length - 1}` } },
          priority,
        )
        if (!response.ok) throw new Error(`l'archivio ha risposto ${response.status}`)
        return new Uint8Array(await response.arrayBuffer())
      })()
      taken.set(key, fetching)
      fetching.catch(() => taken.delete(key))
      return fetching
    },
  }
}
